import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlansWithStatus, buySlot } from '../../api/planApi';
import { getMyWallets } from '../../api/walletApi';
import { useAuth } from '../../context/AuthContext';

const cyan = '#00e5ff';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.08) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.2)',
  color: '#fff',
};

const Row = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 15, ...valueStyle }}>{value}</span>
  </div>
);

const BuySlot = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [plan, setPlan] = useState(null);
  const [mainBalance, setMainBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    Promise.all([getPlansWithStatus(), getMyWallets()])
      .then(([plansRes, walletsRes]) => {
        const found = plansRes.data.plans.find((p) => p._id === planId);
        if (!found) { navigate('/slots'); return; }
        setPlan(found);
        setMainBalance(walletsRes.data.wallets.main_wallet?.balance ?? 0);
      })
      .finally(() => setLoading(false));
  }, [planId, navigate]);

  const insufficient = plan && mainBalance !== null && mainBalance < plan.amount;

  const handleBuy = async () => {
    setError('');
    setBuying(true);
    try {
      const res = await buySlot(planId);
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/slots'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 480 }}>
        <button
          onClick={() => navigate('/slots')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Slots
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>Confirm Purchase</h5>

        {success ? (
          <Card style={cardDark} className="p-4 text-center">
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 16 }}>Slot Purchased Successfully!</div>
            <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>Redirecting to slots...</div>
          </Card>
        ) : (
          <Card style={cardDark} className="p-4">
            <Row label="Main Wallet Balance" value={`${mainBalance} USDT`} valueStyle={{ color: cyan }} />
            <Row label="Slot Name" value={plan?.planName} valueStyle={{ color: '#fff' }} />
            <Row label="Amount" value={`${plan?.amount} USDT`} valueStyle={{ color: '#66ff99' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Total Payable</span>
              <span style={{ fontWeight: 800, fontSize: 18, color: '#66ff99' }}>{plan?.amount} USDT</span>
            </div>

            {insufficient && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                Insufficient balance. You need <strong>{plan.amount} USDT</strong> but have <strong>{mainBalance} USDT</strong>.
              </Alert>
            )}
            {plan?.purchased && (
              <Alert variant="warning" style={{ fontSize: 13 }}>
                You have already purchased this slot.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                {error}
              </Alert>
            )}

            <Button
              onClick={handleBuy}
              disabled={buying || insufficient || plan?.purchased}
              style={{
                background: insufficient || plan?.purchased ? 'rgba(255,255,255,0.05)' : cyan,
                border: 'none',
                color: insufficient || plan?.purchased ? '#555' : '#000',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px',
                borderRadius: 8,
                marginTop: 8,
                width: '100%',
              }}
            >
              {buying ? <Spinner animation="border" size="sm" /> : 'Buy Slot'}
            </Button>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default BuySlot;
